import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  ErrorState,
  Pagination,
  Table,
} from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import { usePageQueryParam } from '@/shared/hooks/use-page-query-param';
import type { GuaranteeCampaignListItem } from '../../domain/entities/guarantee.entity';
import { useGuaranteeCampaigns } from '../hooks/use-guarantee-campaigns';
import { GuaranteeCampaignFormModal } from '../components/GuaranteeCampaignFormModal';
import { formatDate } from '../format';

const PER_PAGE = 20;

export function GuaranteeCampaignsPage() {
  const navigate = useNavigate();
  const [page, setPage] = usePageQueryParam();
  const [creating, setCreating] = useState(false);
  const { data, isLoading, isError, refetch } = useGuaranteeCampaigns(
    page,
    PER_PAGE,
  );

  const columns: ColumnDef<GuaranteeCampaignListItem>[] = [
    {
      key: 'name',
      header: 'Nome',
      render: (row) => (
        <button
          type="button"
          onClick={() => navigate(`/guarantee/campaigns/${row.id}`)}
          className="text-(--color-text-primary) text-left font-medium hover:underline"
        >
          {row.name}
        </button>
      ),
    },
    { key: 'offerCount', header: 'Ofertas', render: (row) => row.offerCount },
    {
      key: 'activeGuarantees',
      header: 'Pessoas ativas',
      render: (row) => row.activeGuarantees,
    },
    {
      key: 'createdAt',
      header: 'Criada em',
      render: (row) => (
        <span className="text-(--color-text-secondary)">
          {formatDate(row.createdAt)}
        </span>
      ),
    },
  ];

  const isEmpty = !isLoading && !isError && data?.total === 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            Campanhas de garantia
          </h1>
          <p className="text-(--color-text-secondary) mt-1 max-w-3xl text-sm">
            Cada campanha diz quais ofertas abrem a garantia estendida do MedQ e
            que régua o aluno precisa cumprir.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>Nova campanha</Button>
      </div>

      {isError ? (
        <ErrorState
          message="Não foi possível carregar as campanhas."
          onRetry={() => void refetch()}
        />
      ) : isEmpty ? (
        <div className="border-(--color-border) bg-(--color-bg-primary) rounded-xl border p-8 text-center">
          <p className="text-(--color-text-secondary) text-sm">
            Nenhuma campanha ainda.
          </p>
          <Button className="mt-4" onClick={() => setCreating(true)}>
            Criar a primeira campanha
          </Button>
        </div>
      ) : (
        <div className="border-(--color-border) bg-(--color-bg-primary) space-y-5 rounded-xl border p-2 sm:p-6">
          <Table
            columns={columns}
            data={data?.items ?? []}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            emptyMessage="Nenhuma campanha nesta página."
            striped
          />
          {data && (
            <Pagination
              page={page}
              total={data.total}
              perPage={PER_PAGE}
              loading={isLoading}
              label="campanha"
              onPageChange={setPage}
            />
          )}
        </div>
      )}

      <GuaranteeCampaignFormModal
        open={creating}
        onClose={() => setCreating(false)}
      />
    </div>
  );
}
